// 导入必要的React钩子和组件
import React, { useState, useEffect, useMemo, useCallback } from "react";
import { DownOutlined, RightOutlined } from "@ant-design/icons";
import { Button, Collapse } from "antd";
import equal from "fast-deep-equal"; // 用于深度比较对象是否相等
import CardGroup from "../CardGroup/CardGroup";
import ChangeLog from "../ChangeLog/ChangeLog";
import Loading from "../Loading/Loading";
import { fetchFilesList } from "../../api/services";
import styles from "./index.module.less";
import { useIntl } from "react-intl";
import { FormattedMessage } from "react-intl";

const { Panel } = Collapse;

// 定义手册下载链接的配置对象
// 根据不同的区域（中国/全球）提供不同的下载链接
const HANDBOOK_DOWNLOAD_LINKS = {
  // 工业数字化手册
  IndustrialDigitalization_handbook: {
    china: [
      {
        name: "工业数字化产品合集",
        url: "https://www.inhand.com.cn/wp-content/uploads/Industrial-digitalization.pdf",
      },
      {
        name: "边缘智能解决方案",
        url: "https://www.inhand.com.cn/wp-content/uploads/Edge-Intelligence-Solution.pdf",
      },
    ],
    global: [
      {
        name: "Product Catalog - Industrial IoT",
        url: "https://www.inhand.com/wp-content/uploads/product-catalog_industrial-iot.pdf",
      },
      {
        name: "Brochure - InHand Edge Intelligence Solution",
        url: "https://www.inhand.com/wp-content/uploads/inhand-edge-intelligence-solution.pdf",
      },
    ],
  },
  // 企业网络手册
  EnterpriseNetwork_handbook: {
    china: [
      {
        name: "企业网络解决方案",
        url: "https://www.inhand.com.cn/wp-content/uploads/enterprise-network.pdf",
      },
    ],
    global: [
      {
        name: "Brochure - Enterprise Network",
        url: "https://www.inhand.com/wp-content/uploads/inhand-enterprise-network.pdf",
      },
    ],
  },
  // 智慧商业手册
  SmartCommerce_handbook: {
    china: [
      {
        name: "智慧新零售产品合集",
        url: "https://www.inhand.com.cn/wp-content/uploads/smart-commerce.pdf",
      },
    ],
    global: [
      {
        name: "InHandGo Grab & Go AI Vending Cooler",
        url: "https://www.inhand.com/wp-content/uploads/inhandgo-grab-n-go-ai-vending-cooler.pdf",
      },
      {
        name: "Smart Vending System",
        url: "https://www.inhand.com/wp-content/uploads/inhand-smart-vending-system.pdf",
      },
    ],
  },
  // 数字能源手册
  DigitalEnergy_handbook: {
    china: [
      {
        name: "智能化配电网线路状态监测系统",
        url: "https://www.inhand.com.cn/wp-content/uploads/intelligent-wireless-observing-system.pdf",
      },
    ],
    global: [
      {
        name: "IWOS Brochure",
        url: "https://www.inhand.com/wp-content/uploads/iwos.pdf",
      },
    ],
  },
  // 移动性手册
  Mobility_handbook: {
    china: [
      {
        name: "车载与运输产品合集",
        url: "https://www.inhand.com.cn/wp-content/uploads/mobility.pdf",
      },
    ],
    global: [
      {
        name: "Product Catalog - Mobility",
        url: "https://www.inhand.com/wp-content/uploads/product-catalog_mobility.pdf",
      },
      {
        name: "InVision ADAS Solution",
        url: "https://www.inhand.com/wp-content/uploads/invision.pdf",
      },
    ],
  },
};

// 将项目按照每4个一组进行分组的辅助函数
const groupItems = items => {
  return items.reduce((result, item, index) => {
    const groupIndex = Math.floor(index / 4);
    if (!result[groupIndex]) {
      result[groupIndex] = [];
    }
    result[groupIndex].push(item);
    return result;
  }, []);
};

// 按子类别对文件进行分组的辅助函数
const groupBySubcategory = files => {
  return files.reduce((acc, file) => {
    const subcategory = file.subcategory;
    if (!acc[subcategory]) {
      acc[subcategory] = [];
    }
    acc[subcategory].push(file);
    return acc;
  }, {});
};

// FilesTile组件：用于显示文件列表的主要组件
function FilesTile({ title, params, changelogMaps = [] }) {
  const intl = useIntl(); // 初始化国际化
  const firmwareText = intl.formatMessage({ id: "Firmware" });

  // 状态管理
  const [filesList, setFilesList] = useState([]); // 文件列表
  const [showLoading, setShowLoading] = useState(true); // 加载状态
  const [showBtn, setShowBtn] = useState(true); // 是否显示"更多"按钮
  const [Array, setArray] = useState([]); // 是否显示"更多"按钮

  // 分页配置
  const [pagination, setPagination] = useState({
    pageNumber: 1,
    pageSize: 8,
  });

  // 判断是否为手册、开发者工具或方案类型
  const isHandbook = params.category === "handbook";
  const isDeveloperTools = params.category === "DeveloperTools";
  const isUseCases = params.category === "Use Cases";

  // 获取国际化文本
  const otherText = intl.formatMessage({ id: "other" });
  const fileCountText = (count) => intl.formatMessage({ id: "fileCount" }, { count });

  // 获取手册文件的回调函数
  const getHandbookFiles = useCallback(() => {
    const handbookKey = params.series_id;
    // 根据域名判断区域
    const region =
      window.location.hostname.includes(".cn") ||
      window.location.hostname === "localhost"
        ? "china"
        : "global";

    // 返回对应区域的手册文件列表
    if (HANDBOOK_DOWNLOAD_LINKS[handbookKey]?.[region]) {
      return HANDBOOK_DOWNLOAD_LINKS[handbookKey][region].map(item => ({
        id: `${handbookKey}_${item.name}`,
        name: item.name,
        downloadUrl: item.url,
        external_url: item.url,
      }));
    }
    return [];
  }, [params.series_id]);

  // 请求参数配置
  const parameters = useMemo(
    () => ({
      ...params,
      pageSize: pagination.pageSize,
      pageNumber: pagination.pageNumber,
      product_category: localStorage.getItem("openType"),
    }),
    [pagination, params]
  );

  // 获取文件列表数据
  useEffect(() => {
    if (isHandbook) {
      // 如果是手册类型，直接获取手册文件
      const handbookFiles = getHandbookFiles();
      setFilesList(handbookFiles);
      setShowLoading(false);
      setShowBtn(false);
    } else {
      // 否则从API获取文件列表
      fetchFilesList(parameters).then(data => {
        if (data.result.length === 0) {
          setShowBtn(false);
        }
        setFilesList(prevData => {
          if (!equal(prevData, data.result)) {
            return [...prevData, ...data.result];
          }
          return prevData;
        });
        setShowLoading(false);
      });
    }
  }, [parameters, isHandbook, getHandbookFiles]);

  // Use Cases 展开状态管理（单选模式，只能展开一个）
  const [expandedUseCasesGroup, setExpandedUseCasesGroup] = useState(null);

  // 切换 Use Cases 分组展开状态
  const toggleUseCasesGroup = (subcategory) => {
    setExpandedUseCasesGroup(prev =>
      prev === subcategory ? null : subcategory
    );
  };

  // 处理文件内容分组
  const groupedContent = useMemo(() => {
    if (isDeveloperTools || isUseCases) {
      // 按 subcategory_desc 分组（Use Cases 用 subcategory_desc，DeveloperTools 用 subcategory）
      const subcategoryGroups = filesList.reduce((acc, file) => {
        const key = isUseCases ? file.subcategory_desc : file.subcategory;
        const subcategory = key || (isUseCases ? otherText : 'default');
        if (!acc[subcategory]) {
          acc[subcategory] = [];
        }
        acc[subcategory].push(file);
        return acc;
      }, {});

      // 获得排序后的数组
      const sortedContent = Object.entries(subcategoryGroups)
        .map(([subcategory, files]) => ({
          subcategory,
          groupedFiles: groupItems(files),
          totalFiles: files.length,
        }))
        .sort((a, b) => a.totalFiles - b.totalFiles);

      // 直接从排序后的内容中提取子分类名称
      const sortedSubcategories = sortedContent.map(item => item.subcategory);
      setArray(sortedSubcategories); // 设置排序后的数组

      return sortedContent;
    } else {
      // 否则作为单一组处理
      return [
        {
          subcategory: null,
          groupedFiles: groupItems(filesList),
        },
      ];
    }
  }, [filesList, isDeveloperTools, isUseCases]);

  // const groupedChangelogsList = useMemo(() => {
  //   if (isDeveloperTools) {
  //     const changelogGroups = Array.map(subcategory => ({
  //       subcategory,
  //       changelogs: [
  //         Array.map(subcategory => ({ [subcategory]: subcategory })),
  //       ],
  //     }));
  //     return changelogGroups;
  //   }
  //   return [{ subcategory: null, changelogs: groupItems(changelogMaps) }];
  // }, [changelogMaps, isDeveloperTools, Array]);

  const groupedChangelogsList = useMemo(() => {
    if (isDeveloperTools) {
      // 直接使用 Array（子分类数组）创建 changelogs
      return [
        {
          subcategory: null,
          changelogs: [
            Array.map(subcategory => ({ [subcategory]: subcategory })),
          ],
        },
      ];
    }
    return [{ subcategory: null, changelogs: groupItems(changelogMaps) }];
  }, [changelogMaps, isDeveloperTools, Array]);

  // 处理"更多"按钮点击事件
  const handleMoreClick = () => {
    setPagination(prevPagination => ({
      ...prevPagination,
      pageNumber: prevPagination.pageNumber + 1,
    }));
  };

  // 渲染组件
  return (
    <>
      {filesList.length > 0 ? (
        <div className={styles.flies_group}>
          {/* 标题区域 */}
          <div className={styles.group_header}>
            {isHandbook ? <FormattedMessage id="handbook" /> : title}
          </div>

          {/* 更新日志区域 */}
          {changelogMaps.length > 0 &&
            (title === firmwareText || isDeveloperTools) && (
              <div className="change_log">
                {groupedChangelogsList?.map(
                  ({ subcategory, changelogs }, groupIndex) => (
                    <div key={groupIndex}>
                      <div
                        className={styles.files_rows}
                        style={{ marginBottom: 20 }}
                      >
                        {changelogs.map((changelogGroup, index) => (
                          <ChangeLog
                            key={index}
                            changelogs={changelogGroup}
                            isDeveloperTools={isDeveloperTools}
                            category={params.category}
                            series_id={params.series_id}
                          />
                        ))}
                      </div>
                    </div>
                  )
                )}
              </div>
            )}

          {/* 文件列表区域 */}
          {isUseCases ? (
            /* Use Cases 分类：每排4个标题，点击后在当前排下方展开 */
            <div className={styles.useCasesContainer}>
              {/* 按每4个分组一排来渲染 */}
              {(() => {
                const rows = [];
                for (let rowIndex = 0; rowIndex < Math.ceil(groupedContent.length / 4); rowIndex++) {
                  const rowGroups = groupedContent.slice(rowIndex * 4, rowIndex * 4 + 4);
                  rows.push((() => {
                // 检查当前排是否有展开的分组
                const hasExpandedInRow = rowGroups.some(g => expandedUseCasesGroup ===(g.subcategory));

                return (
                  <div key={rowIndex} className={styles.useCasesRowWrapper}>
                    {/* 当前排的标题 */}
                    <div className={styles.useCasesHeaderRow}>
                      {rowGroups.map(({ subcategory, totalFiles }) => {
                        const isExpanded = expandedUseCasesGroup ===(subcategory);
                        return (
                          <div
                            key={subcategory}
                            className={`${styles.useCasesHeaderCard} ${isExpanded ? styles.useCasesHeaderCardActive : ''}`}
                            onClick={() => toggleUseCasesGroup(subcategory)}
                          >
                            <div className={styles.useCasesHeaderContent}>
                              <span className={styles.useCasesHeaderTitle}>{subcategory}</span>
                              <span className={styles.useCasesHeaderCount}>{fileCountText(totalFiles)}</span>
                            </div>
                            <div className={styles.useCasesHeaderIcon}>
                              {isExpanded ? <DownOutlined /> : <RightOutlined />}
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {/* 当前排展开的内容 */}
                    {hasExpandedInRow && (
                      <div className={styles.useCasesContent}>
                        {rowGroups.map(({ subcategory, groupedFiles }) => {
                          const isExpanded = expandedUseCasesGroup ===(subcategory);
                          if (!isExpanded) return null;
                          return (
                            <div key={`content-${subcategory}`} className={styles.useCasesContentSection}>
                              <div className={styles.files_rows}>
                                {groupedFiles.map((files, index) => (
                                  <CardGroup
                                    key={index}
                                    files={files}
                                    title={title}
                                    isHandbook={isHandbook}
                                  />
                                ))}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })());
                }
                return rows;
              })()}
            </div>
          ) : (
            /* 其他分类直接展示 */
            groupedContent.map(({ subcategory, groupedFiles }, groupIndex) => (
              <div key={groupIndex}>
                {isDeveloperTools && subcategory && (
                  <div className={styles.subcategory_header}></div>
                )}
                <div className={styles.files_rows}>
                  {groupedFiles.map((files, index) => (
                    <CardGroup
                      key={index}
                      files={files}
                      title={isHandbook ? "产品宣传册" : title}
                      isHandbook={isHandbook}
                    />
                  ))}
                </div>
              </div>
            ))
          )}

          {/* "更多"按钮 */}
          {!isHandbook && filesList.length >= 8 && showBtn && (
            <div style={{ textAlign: "center", marginTop: "2%" }}>
              <Button
                type="dash"
                size="large"
                onClick={handleMoreClick}
                style={{
                  border: "1px solid #d9d9d9",
                  width: 120,
                  height: 40,
                }}
              >
                More
                <DownOutlined />
              </Button>
            </div>
          )}
        </div>
      ) : (
        showLoading && <Loading />
      )}
    </>
  );
}

// 使用React.memo优化组件，只有当props发生深度变化时才重新渲染
export default React.memo(FilesTile, equal);
